import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, CircularProgress, Alert, Chip,
  Accordion, AccordionSummary, AccordionDetails, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  IconButton, Tooltip, Divider,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { offersAPI, equipmentsAPI, costEntriesAPI, configAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Chips de estado
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_LABELS = { draft: 'Borrador', sent: 'Enviada', approved: 'Aprobada', rejected: 'Rechazada' };
const STATUS_COLORS = { draft: 'default', sent: 'info', approved: 'success', rejected: 'error' };

// ─────────────────────────────────────────────────────────────────────────────
// Columnas de la tabla de equipos (solo lectura / edición)
// ─────────────────────────────────────────────────────────────────────────────
const EQ_COLS = ['Tag', 'Equipo', 'Capacidad', 'Material', 'Potencia', 'Proveedor',
  'Coste Real (€)', 'Coste Oferta (€)', 'Plazo (sem)'];
const EQ_FIELDS = [
  { field: 'tag', label: 'Tag (ej: TK-001)' },
  { field: 'name', label: 'Nombre del equipo', required: true },
  { field: 'description', label: 'Descripción', fullWidth: true },
  { field: 'capacity', label: 'Capacidad / Tamaño' },
  { field: 'material', label: 'Material constructivo' },
  { field: 'power', label: 'Potencia instalada' },
  { field: 'dimensions', label: 'Dimensiones' },
  { field: 'supplier', label: 'Proveedor' },
  { field: 'supplier_reference', label: 'Referencia proveedor' },
  { field: 'estimated_cost', label: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', label: 'Coste Oferta (EUR)', type: 'number' },
  { field: 'delivery_weeks', label: 'Plazo entrega (sem)', type: 'number' },
  { field: 'notes', label: 'Notas', multiline: true, rows: 3, fullWidth: true },
];

const CE_COLS = ['Concepto / Partida', 'Coste Real (€)', 'Coste Oferta (€)'];
const CE_FIELDS = [
  { field: 'concept', label: 'Concepto / Partida', required: true, fullWidth: true, type: 'autocomplete-freesolo' },
  { field: 'real_cost', label: 'Coste Real (EUR)', type: 'number' },
  { field: 'offer_cost', label: 'Coste Oferta (EUR)', type: 'number' },
  { field: 'notes', label: 'Notas', multiline: true, rows: 2, fullWidth: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// Tabla simple de equipos (lectura, usada para Oferta 1)
// ─────────────────────────────────────────────────────────────────────────────
function EquipmentReadTable({ rows }) {
  if (!rows.length) return (
    <Typography color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
      No hay equipos definidos en ingeniería básica.
    </Typography>
  );
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#c62828' }}>
            {EQ_COLS.map((h) => <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.tag || '-'}</TableCell>
              <TableCell><strong>{r.name}</strong></TableCell>
              <TableCell>{r.capacity || '-'}</TableCell>
              <TableCell>{r.material || '-'}</TableCell>
              <TableCell>{r.power || '-'}</TableCell>
              <TableCell>{r.supplier || '-'}</TableCell>
              <TableCell>{r.estimated_cost != null ? r.estimated_cost.toLocaleString('es-ES') : '-'}</TableCell>
              <TableCell>{r.offer_cost != null ? r.offer_cost.toLocaleString('es-ES') : '-'}</TableCell>
              <TableCell>{r.delivery_weeks || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabla simple de partidas de coste (lectura, usada para Oferta 1)
// ─────────────────────────────────────────────────────────────────────────────
function CostReadTable({ rows }) {
  if (!rows.length) return (
    <Typography color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
      No hay partidas de coste definidas en ingeniería básica.
    </Typography>
  );
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#c62828' }}>
            {CE_COLS.map((h) => <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.concept}</TableCell>
              <TableCell>{r.real_cost != null ? r.real_cost.toLocaleString('es-ES') : '-'}</TableCell>
              <TableCell>{r.offer_cost != null ? r.offer_cost.toLocaleString('es-ES') : '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabla editable inline (para Oferta N > 1)
// ─────────────────────────────────────────────────────────────────────────────
function EditableOfferTable({ title, cols, fields, rows, onAdd, onUpdate, onDelete, disabled, conceptOptions }) {
  const emptyRow = () => Object.fromEntries(fields.map((f) => [f.field, '']));
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // null = create, row = edit
  const [form, setForm] = useState(emptyRow());

  const openCreate = () => { setForm(emptyRow()); setEditing(null); setDlgOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setEditing(row); setDlgOpen(true); };
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    if (editing) {
      await onUpdate(editing.id, form);
    } else {
      await onAdd(form);
    }
    setDlgOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        {!disabled && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
            Añadir
          </Button>
        )}
      </Box>

      {rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No hay entradas. Haz clic en Añadir para crear la primera.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {cols.map((h) => <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>)}
                {!disabled && <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  {fields.slice(0, cols.length).map((f) => (
                    <TableCell key={f.field}>
                      {r[f.field] != null && r[f.field] !== '' ? (
                        f.type === 'number'
                          ? Number(r[f.field]).toLocaleString('es-ES')
                          : r[f.field]
                      ) : '-'}
                    </TableCell>
                  ))}
                  {!disabled && (
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => onDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar entrada' : 'Nueva entrada'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {fields.map((f) => (
              <Grid item xs={12} sm={f.fullWidth ? 12 : 6} key={f.field}>
                {f.type === 'autocomplete-freesolo' ? (
                  <Autocomplete
                    freeSolo
                    options={conceptOptions || []}
                    value={form[f.field] ?? ''}
                    onChange={(_, newValue) => setForm({ ...form, [f.field]: typeof newValue === 'string' ? newValue : (newValue || '') })}
                    onInputChange={(_, newInputValue) => setForm({ ...form, [f.field]: newInputValue })}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label={f.label} required={f.required} />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={f.label}
                    name={f.field}
                    type={f.type || 'text'}
                    multiline={f.multiline}
                    rows={f.rows}
                    value={form[f.field] ?? ''}
                    onChange={handleChange}
                    required={f.required}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={fields.some((f) => f.required && !form[f.field])}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sección de una oferta individual
// ─────────────────────────────────────────────────────────────────────────────
function OfferSection({
  offer, baseEquipments, baseCostEntries, onOfferUpdated, onOfferDeleted, projectType, partidas,
}) {
  const isFirst = offer.offer_number === 1;
  const isApproved = offer.status === 'approved';

  const { isAdmin } = useAuth();

  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ title: offer.title || '', notes: offer.notes || '' });
  const [equipments, setEquipments] = useState([]);
  const [costEntries, setCostEntries] = useState([]);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnapprove, setConfirmUnapprove] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadOfferData = useCallback(async () => {
    if (isFirst) return;  // Oferta 1 usa los datos de basic engineering del padre
    try {
      const [eqResp, ceResp] = await Promise.all([
        offersAPI.listEquipments(offer.id),
        offersAPI.listCostEntries(offer.id),
      ]);
      setEquipments(eqResp.data);
      setCostEntries(ceResp.data);
    } catch (err) {
      console.error('Error loading offer data:', err);
    }
  }, [offer.id, isFirst]);

  useEffect(() => { loadOfferData(); }, [loadOfferData]);

  const handleSaveHeader = async () => {
    try {
      const resp = await offersAPI.update(offer.id, headerForm);
      onOfferUpdated(resp.data);
      setEditingHeader(false);
    } catch (err) {
      setError('Error al guardar la oferta.');
    }
  };

  const handleApprove = async () => {
    setLoadingApprove(true);
    setError(null);
    try {
      const resp = await offersAPI.approve(offer.id);
      onOfferUpdated(resp.offer || resp.data?.offer);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al aprobar la oferta.');
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleUnapprove = async () => {
    setConfirmUnapprove(false);
    setLoadingApprove(true);
    try {
      await offersAPI.unapprove(offer.id);
      onOfferUpdated(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al revertir la aprobación.');
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleDelete = async () => {
    try {
      await offersAPI.delete(offer.id);
      onOfferDeleted(offer.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar la oferta.');
    }
    setConfirmDelete(false);
  };

  // Equipos de la oferta (para N > 1)
  const eqHandlers = {
    onAdd: async (data) => { await offersAPI.createEquipment(offer.id, data); await loadOfferData(); },
    onUpdate: async (id, data) => { await offersAPI.updateEquipment(id, data); await loadOfferData(); },
    onDelete: async (id) => { await offersAPI.deleteEquipment(id); await loadOfferData(); },
  };

  const ceHandlers = {
    onAdd: async (data) => { await offersAPI.createCostEntry(offer.id, data); await loadOfferData(); },
    onUpdate: async (id, data) => { await offersAPI.updateCostEntry(id, data); await loadOfferData(); },
    onDelete: async (id) => { await offersAPI.deleteCostEntry(id); await loadOfferData(); },
  };

  const totalEquipRealCost = (isFirst ? baseEquipments : equipments)
    .reduce((s, e) => s + (parseFloat(e.estimated_cost) || 0), 0);
  const totalEquipOfferCost = (isFirst ? baseEquipments : equipments)
    .reduce((s, e) => s + (parseFloat(e.offer_cost) || 0), 0);
  const totalCostRealCost = (isFirst ? baseCostEntries : costEntries)
    .reduce((s, e) => s + (parseFloat(e.real_cost) || 0), 0);
  const totalCostOfferCost = (isFirst ? baseCostEntries : costEntries)
    .reduce((s, e) => s + (parseFloat(e.offer_cost) || 0), 0);
  const grandRealTotal = totalEquipRealCost + totalCostRealCost;
  const grandTotal = totalEquipOfferCost + totalCostOfferCost;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, v) => setExpanded(v)}
      sx={{ mb: 2, border: isApproved ? '1px solid #4caf50' : '1px solid #e0e0e0' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: isApproved ? '#f1f8f1' : '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
          <Typography fontWeight="bold" sx={{ minWidth: 90 }}>
            Oferta {offer.offer_number}
          </Typography>
          <Typography sx={{ flexGrow: 1, color: 'text.secondary' }}>
            {offer.title || (isFirst ? 'Oferta principal (Ingeniería Básica)' : 'Sin título')}
          </Typography>
          {grandTotal > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {grandTotal.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </Typography>
          )}
          <Chip
            label={STATUS_LABELS[offer.status] || offer.status}
            color={STATUS_COLORS[offer.status] || 'default'}
            size="small"
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Cabecera editable */}
        <Box sx={{ mb: 3 }}>
          {editingHeader ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Título de la oferta"
                  value={headerForm.title}
                  onChange={(e) => setHeaderForm({ ...headerForm, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notas / Justificación de la oferta"
                  value={headerForm.notes}
                  onChange={(e) => setHeaderForm({ ...headerForm, notes: e.target.value })}
                  placeholder="Describe la razón de esta oferta, cambios solicitados, aclaraciones, etc."
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSaveHeader} sx={{ mr: 1 }}>
                  Guardar
                </Button>
                <Button size="small" startIcon={<CancelIcon />} onClick={() => {
                  setHeaderForm({ title: offer.title || '', notes: offer.notes || '' });
                  setEditingHeader(false);
                }}>
                  Cancelar
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Box>
              {offer.notes && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fffde7' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notas / Justificación
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{offer.notes}</Typography>
                </Paper>
              )}
              {!offer.notes && !isApproved && (
                <Typography color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Sin notas. Edita para añadir la justificación de esta oferta.
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!isApproved && (
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditingHeader(true)}>
                    Editar notas / título
                  </Button>
                )}
                {!isApproved && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={loadingApprove ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                    onClick={handleApprove}
                    disabled={loadingApprove}
                  >
                    Aprobar oferta
                  </Button>
                )}
                {isApproved && (
                  <Chip label="✓ Oferta aprobada" color="success" variant="outlined" />
                )}
                {isApproved && isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={loadingApprove}
                    onClick={() => setConfirmUnapprove(true)}
                  >
                    Revertir aprobación
                  </Button>
                )}
                {!isFirst && !isApproved && (
                  <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />}
                    onClick={() => setConfirmDelete(true)}>
                    Eliminar oferta
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Lista de equipos */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Lista de Equipos
        </Typography>
        {isFirst ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Los equipos de la oferta principal provienen de la Ingeniería Básica y se actualizan
              automáticamente al modificar equipos en esa sección.
            </Typography>
            <EquipmentReadTable rows={baseEquipments} />
          </>
        ) : (
          <EditableOfferTable
            title=""
            cols={EQ_COLS.slice(0, 8)}
            fields={EQ_FIELDS}
            rows={equipments}
            disabled={isApproved}
            {...eqHandlers}
          />
        )}

        <Box sx={{ mt: 3 }} />
        <Divider sx={{ mb: 2 }} />

        {/* Partidas de coste */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Estimación de Costes por Partida
        </Typography>
        {isFirst ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Las partidas de coste de la oferta principal provienen de la Ingeniería Básica y se
              actualizan automáticamente al modificar partidas en esa sección.
            </Typography>
            <CostReadTable rows={baseCostEntries} />
          </>
        ) : (
          <EditableOfferTable
            title=""
            cols={CE_COLS}
            fields={CE_FIELDS}
            rows={costEntries}
            disabled={isApproved}
            conceptOptions={partidas || []}
            {...ceHandlers}
          />
        )}

        {/* Totales */}
        {(grandTotal > 0 || grandRealTotal > 0) && (
          <Paper sx={{ p: 2, mt: 3, backgroundColor: '#c62828', color: '#fff' }}>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Equipos (coste real)</Typography>
                <Typography fontWeight="bold" sx={{ color: '#fff' }}>
                  {totalEquipRealCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Equipos (coste oferta)</Typography>
                <Typography fontWeight="bold" sx={{ color: '#fff' }}>
                  {totalEquipOfferCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Partidas (coste real)</Typography>
                <Typography fontWeight="bold" sx={{ color: '#fff' }}>
                  {totalCostRealCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Partidas (coste oferta)</Typography>
                <Typography fontWeight="bold" sx={{ color: '#fff' }}>
                  {totalCostOfferCost.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Total real</Typography>
                <Typography fontWeight="bold" sx={{ color: '#fff' }}>
                  {grandRealTotal.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>Total oferta</Typography>
                <Typography fontWeight="bold" sx={{ color: '#a5d6a7' }}>
                  {grandTotal.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </AccordionDetails>

      {/* Diálogo confirmar eliminar */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar oferta {offer.offer_number}</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta oferta y todos sus equipos y partidas?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmar revertir aprobación */}
      <Dialog open={confirmUnapprove} onClose={() => setConfirmUnapprove(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Revertir aprobación — Oferta {offer.offer_number}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            ¿Estás seguro de que deseas revertir la aprobación de esta oferta?
          </Typography>
          {offer.offer_number > 1 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Los equipos y partidas transferidos a las listas generales al aprobar esta oferta
              serán eliminados de esas listas.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnapprove(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleUnapprove}>Revertir</Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export default function Offers({ projectId, projectType }) {
  const [offers, setOffers] = useState([]);
  const [baseEquipments, setBaseEquipments] = useState([]);   // equipos de ing. básica (offer_id=null)
  const [baseCostEntries, setBaseCostEntries] = useState([]); // partidas de ing. básica (offer_id=null)
  const [partidas, setPartidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const [offersResp, eqResp, ceResp] = await Promise.all([
        offersAPI.list(projectId),
        equipmentsAPI.list(projectId),
        costEntriesAPI.list(projectId),
      ]);
      setOffers(offersResp.data);
      // Equipos y partidas de la ingeniería básica (sin oferta suplementaria asignada)
      setBaseEquipments(eqResp.data.filter((e) => !e.offer_id));
      setBaseCostEntries(ceResp.data.filter((e) => !e.offer_id));
      // Cargar las partidas de coste del tipo de proyecto para el autocompletado
      if (projectType) {
        configAPI.listCostPartidas(projectType)
          .then((resp) => setPartidas((resp.data || []).map((p) => p.name)))
          .catch(() => {});
      }
      setError(null);
    } catch (err) {
      console.error('Error loading offers:', err);
      setError('Error al cargar las ofertas.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreateOffer = async () => {
    setCreating(true);
    try {
      const nextNum = offers.length + 1;
      const resp = await offersAPI.create(projectId, {
        title: `Oferta ${nextNum}`,
        notes: '',
        status: 'draft',
      });
      setOffers((prev) => [...prev, resp.data]);
    } catch (err) {
      setError('Error al crear la oferta.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFirstOffer = async () => {
    setCreating(true);
    try {
      const resp = await offersAPI.create(projectId, {
        title: 'Oferta principal',
        notes: '',
        status: 'draft',
        offer_number: 1,
      });
      setOffers([resp.data]);
    } catch (err) {
      setError('Error al crear la oferta principal.');
    } finally {
      setCreating(false);
    }
  };

  const handleOfferUpdated = (updatedOffer) => {
    if (!updatedOffer) { loadAll(); return; }
    setOffers((prev) => prev.map((o) => o.id === updatedOffer.id ? updatedOffer : o));
    // Si se aprobó una oferta suplementaria, recargar la lista de equipos y partidas base
    loadAll();
  };

  const handleOfferDeleted = (offerId) => {
    setOffers((prev) => prev.filter((o) => o.id !== offerId));
  };

  if (!projectId) return <Alert severity="warning">No se ha seleccionado un proyecto.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Ofertas del Proyecto</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona todas las ofertas generadas para este proyecto. La primera oferta corresponde a
            la ingeniería básica. Las siguientes se generan durante la ejecución del proyecto cuando
            surgen nuevas necesidades.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          onClick={offers.length === 0 ? handleCreateFirstOffer : handleCreateOffer}
          disabled={creating}
          sx={{ ml: 2, flexShrink: 0 }}
        >
          {offers.length === 0 ? 'Registrar oferta principal' : 'Nueva oferta suplementaria'}
        </Button>
      </Box>

      {offers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc' }}>
          <Typography color="text.secondary">
            No hay ofertas registradas. Pulsa "Registrar oferta principal" para vincular la oferta
            inicial generada desde la Ingeniería Básica.
          </Typography>
        </Paper>
      ) : (
        offers.map((offer) => (
          <OfferSection
            key={offer.id}
            offer={offer}
            baseEquipments={baseEquipments}
            baseCostEntries={baseCostEntries}
            onOfferUpdated={handleOfferUpdated}
            onOfferDeleted={handleOfferDeleted}
            projectType={projectType}
            partidas={partidas}
          />
        ))
      )}
    </Box>
  );
}
